import { ref } from 'vue';
import { Sentry } from '@/services/sentry';
import { App as CapApp } from '@capacitor/app';
import { useRouter } from 'vue-router';
import { useFeedStore } from '@/stores/feed';
import { getWidgetRoute } from '@/utils/widgetTypeMapper';
import { useSubscription } from '@/composables/useSubscription';
import { isRevenueCatConfigured } from '@/services/revenueCat';

const DEEP_LINK_PREFIX = '/app/deep-link';
const FEED_ITEM_PATTERN = /^\/tabs\/feed\/(\d+)$/;
const PREMIUM_PATH = '/premium';

const pendingTabPath = ref(null);
const pendingPremium = ref(false);
const initialized = ref(false);

export const useDeepLinks = () => {
  const router = useRouter();
  const feedStore = useFeedStore();
  const { isPremium, checkPremiumAccess, showPaywall, showCustomerCenter } = useSubscription();

  async function handlePremium() {
    if (!isRevenueCatConfigured()) {
      console.log('[Deep Links] RevenueCat not configured yet, deferring premium deep link');
      pendingPremium.value = true;

      return;
    }

    console.log('[Deep Links] Opening premium screen');

    await checkPremiumAccess();

    if (isPremium.value) {
      await showCustomerCenter();
    } else {
      await showPaywall();
    }
  }

  async function handleFeedItem(id) {
    try {
      const item = await feedStore.fetchItemById(id);
      const widgetPath = getWidgetRoute(item.type, id);

      if (widgetPath) {
        navigateToTabPath(widgetPath);

        return;
      }
    } catch (err) {
      console.error('[Deep Links] Error fetching feed item:', err);
      Sentry.captureException(err);
    }

    navigateToTabPath('/tabs/feed');
  }

  function handleDeepLink(url) {
    if (!url || !url.includes(DEEP_LINK_PREFIX)) {
      return;
    }

    const path = url.split(DEEP_LINK_PREFIX).pop();

    console.log('[Deep Links] Navigating to:', path);

    router.isReady().then(async () => {
      if (path === PREMIUM_PATH) {
        await handlePremium();

        return;
      }

      const feedItemMatch = path.match(FEED_ITEM_PATTERN);

      if (feedItemMatch) {
        await handleFeedItem(feedItemMatch[1]);

        return;
      }

      if (path.startsWith('/tabs/')) {
        navigateToTabPath(path);
      } else {
        router.push(path);
      }
    });
  }

  function navigateToTabPath(path) {
    const currentPath = router.currentRoute.value.path;

    if (currentPath.startsWith('/tabs/') && initialized.value) {
      console.log('[Deep Links] Already in tabs:', path);

      router.push(path);
    } else {
      console.log('[Deep Links] Not in tabs yet, navigating to:', path);

      pendingTabPath.value = path;

      router.push('/tabs/feed');
    }
  }

  const init = () => {
    // Handle deep links when app is already running (warm start)
    CapApp.addListener('appUrlOpen', (event) => {
      console.log('[Deep Links] App opened with URL:', JSON.stringify(event));

      handleDeepLink(event.url);
    });

    // Handle deep links when app is launched via URL (cold start)
    CapApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        console.log('[Deep Links] App launched with URL:', result.url);

        handleDeepLink(result.url);
      }
    });
  };

  const navigateIfPending = (ionRouter) => {
    initialized.value = true;

    if (!pendingTabPath.value) {
      return;
    }

    console.log('[Deep Links] Router ready, navigating to:', pendingTabPath.value);

    ionRouter.replace(pendingTabPath.value);

    pendingTabPath.value = null;
  };

  const processPendingPremium = async () => {
    if (!pendingPremium.value) {
      return;
    }

    pendingPremium.value = false;

    await handlePremium();
  };

  return { init, navigateIfPending, handleDeepLink, processPendingPremium };
};
